/* ============================================================================
   THE SUNYA — site navigation behaviour.

   <sunya-mega-nav>   desktop bar + mega panels (snippets/sunya-nav-desktop,
                      sunya-nav-panels, and the Help trigger in header-icons)
   <sunya-drawer-nav> mobile menu panels + search suggestions
                      (snippets/sunya-nav-drawer, inside <menu-drawer>)

   Disclosure pattern throughout (WAI-ARIA APG "disclosure navigation"), not
   role=menu: these are lists of links, and menu semantics would take arrow
   keys away from screen-reader browsing.
   ========================================================================== */

(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ── Desktop ───────────────────────────────────────────────────────────── */

  // Open after the pointer rests for OPEN_DELAY; once one panel is open, the
  // next opens immediately (the shopper is already browsing). Leaving waits
  // CLOSE_DELAY so a pointer that overshoots the panel edge is forgiven.
  const OPEN_DELAY = 150;
  const CLOSE_DELAY = 300;
  // Moving diagonally from an item down into its panel crosses neighbouring
  // items. While the pointer is heading down, a switch waits this long; if it
  // reaches the panel first, the switch never happens.
  const AIM_DELAY = 120;

  class SunyaMegaNav extends HTMLElement {
    connectedCallback() {
      this.header = this.closest('header');
      if (!this.header) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.items = Array.from(this.header.querySelectorAll('[data-sn-item]'));
      this.panels = new Map();
      this.header.querySelectorAll('[data-sn-panel]').forEach((p) => this.panels.set(p.dataset.snPanel, p));
      this.current = null;
      this.timer = null;
      this.lastY = 0;
      this.movingDown = false;

      this.items.forEach((item) => {
        const key = item.dataset.snItem;
        if (!this.panels.has(key)) return;
        const link = item.querySelector('[data-sn-link]');
        const toggle = item.querySelector('[data-sn-toggle]');

        item.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.intent(key); }, { signal });
        item.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.scheduleClose(); }, { signal });

        toggle?.addEventListener('click', () => {
          this.current === key ? this.close(true) : this.open(key);
        }, { signal });

        // Touch at desktop widths (iPad landscape): the first tap opens the
        // panel, a second tap on the same label follows the link.
        link?.addEventListener('click', (e) => {
          if (canHover() || this.dataset.hover === 'false') return;
          if (this.current !== key) {
            e.preventDefault();
            this.open(key);
          }
        }, { signal });
      });

      // Items without a panel close whatever is open.
      this.querySelectorAll('[data-sn-plain]').forEach((a) => {
        a.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !this.current) return;
          this.clear();
          this.timer = setTimeout(() => this.close(), OPEN_DELAY);
        }, { signal });
      });

      this.panels.forEach((panel) => {
        panel.addEventListener('pointerenter', () => this.clear(), { signal });
        panel.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.scheduleClose(); }, { signal });
      });

      document.addEventListener('pointermove', (e) => {
        this.movingDown = e.clientY > this.lastY;
        this.lastY = e.clientY;
      }, { passive: true, signal });

      // The panels live at the end of the header, so the natural Tab order
      // would skip them. Tab from an open item's toggle goes into its panel,
      // Tab off the panel's last link carries on along the bar, Shift+Tab
      // off its first link returns to the toggle.
      this.header.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || !this.current) return;
        const key = this.current;
        const panel = this.panels.get(key);
        const toggle = this.header.querySelector(`[data-sn-toggle="${key}"]`);
        if (!panel || !toggle) return;
        const inPanel = this.focusables(panel);
        if (!inPanel.length) return;
        if (!e.shiftKey && document.activeElement === toggle) {
          e.preventDefault();
          inPanel[0].focus();
        } else if (!e.shiftKey && document.activeElement === inPanel[inPanel.length - 1]) {
          const order = this.focusables(this.header).filter((el) => !el.closest('[data-sn-panel]'));
          const next = order[order.indexOf(toggle) + 1];
          if (next) { e.preventDefault(); this.close(); next.focus(); }
        } else if (e.shiftKey && document.activeElement === inPanel[0]) {
          e.preventDefault();
          toggle.focus();
        }
      }, { signal });

      // Fetch every panel's photos the first time the shopper shows intent,
      // so a panel never opens onto empty frames. ~150 KB, once.
      const warm = () => {
        this.panels.forEach((panel) => panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }));
      };
      this.addEventListener('pointerenter', warm, { once: true, signal });
      this.header.querySelector('[data-sn-item="help"]')?.addEventListener('pointerenter', warm, { once: true, signal });
      this.addEventListener('focusin', warm, { once: true, signal });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !this.current) return;
        const toggle = this.header.querySelector(`[data-sn-toggle="${this.current}"]`);
        const hadFocusInside = this.panels.get(this.current)?.contains(document.activeElement) ||
          this.header.querySelector(`[data-sn-item="${this.current}"]`)?.contains(document.activeElement);
        this.close();
        if (hadFocusInside) toggle?.focus();
      }, { signal });

      // Focus leaving the bar and the open panel closes it, so Tab never
      // walks into a panel the shopper cannot see, or away from one left open.
      this.header.addEventListener('focusout', (e) => {
        if (!this.current) return;
        const next = e.relatedTarget;
        if (!next) return;
        const item = this.header.querySelector(`[data-sn-item="${this.current}"]`);
        const panel = this.panels.get(this.current);
        if (!item?.contains(next) && !panel?.contains(next)) this.close();
      }, { signal });

      document.addEventListener('click', (e) => {
        if (!this.current) return;
        if (this.header.contains(e.target)) return;
        this.close();
      }, { signal });

      window.addEventListener('scroll', () => {
        if (this.current && Math.abs(window.scrollY - this.openedAt) > 40) this.close();
      }, { passive: true, signal });

      if (window.Shopify?.designMode) {
        this.header.addEventListener('shopify:block:select', (e) => {
          const panel = e.target.closest?.('[data-sn-panel]');
          if (panel) this.open(panel.dataset.snPanel);
        }, { signal });
        this.header.addEventListener('shopify:block:deselect', () => this.close(), { signal });
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.clear();
      document.body.classList.remove('has-dropdown-menu', 'sn-mega-open');
    }

    focusables(root) {
      return Array.from(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((el) => el.offsetParent !== null || el.getClientRects().length);
    }

    clear() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    intent(key) {
      this.clear();
      if (this.current === key) return;
      const delay = !this.current ? OPEN_DELAY : (this.movingDown ? AIM_DELAY : 0);
      if (delay === 0) this.open(key);
      else this.timer = setTimeout(() => this.open(key), delay);
    }

    scheduleClose() {
      this.clear();
      this.timer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    open(key) {
      this.clear();
      const panel = this.panels.get(key);
      if (!panel) return;
      const switching = !!this.current;
      if (this.current && this.current !== key) this.hidePanel(this.current, true);
      this.current = key;
      this.openedAt = window.scrollY;

      if (key === 'help') this.placeHelp(panel);
      panel.hidden = false;
      panel.classList.toggle('is-instant', switching || reduceMotion());
      requestAnimationFrame(() => panel.classList.add('is-open'));

      this.header.querySelectorAll(`[data-sn-toggle="${key}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
      this.header.querySelector(`[data-sn-item="${key}"]`)?.classList.add('is-open');
      document.body.classList.add('has-dropdown-menu', 'sn-mega-open');
    }

    close() {
      this.clear();
      if (!this.current) return;
      this.hidePanel(this.current, false);
      this.current = null;
      document.body.classList.remove('has-dropdown-menu', 'sn-mega-open');
    }

    hidePanel(key, instant) {
      const panel = this.panels.get(key);
      this.header.querySelectorAll(`[data-sn-toggle="${key}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.header.querySelector(`[data-sn-item="${key}"]`)?.classList.remove('is-open');
      if (!panel) return;
      panel.classList.remove('is-open');
      if (instant || reduceMotion()) {
        panel.hidden = true;
        return;
      }
      const done = () => { if (!panel.classList.contains('is-open')) panel.hidden = true; };
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 220);
    }

    // The Help dropdown hangs under its trigger rather than spanning the header.
    placeHelp(panel) {
      const trigger = this.header.querySelector('[data-sn-item="help"]');
      if (!trigger) return;
      const h = this.header.getBoundingClientRect();
      const t = trigger.getBoundingClientRect();
      panel.style.right = `${Math.max(16, h.right - t.right)}px`;
    }
  }

  if (!customElements.get('sunya-mega-nav')) customElements.define('sunya-mega-nav', SunyaMegaNav);

  /* ── Mobile ────────────────────────────────────────────────────────────── */

  class SunyaDrawerNav extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.drawer = this.closest('menu-drawer');
      this.root = this.querySelector('[data-sn-root]');
      this.crumb = this.drawer?.querySelector('[data-sn-crumb]');
      this.wordmark = this.drawer?.querySelector('.sunya-navmark');
      // The drawer moves to <body> while open and back when it closes, which
      // reconnects this element. Read the open panel from the page instead of
      // forgetting it: a null here made the reset after closing skip a panel
      // still on screen, so the menu reopened in it with no way back.
      const opener = this.querySelector('[data-sn-open][aria-expanded="true"]');
      const open = this.querySelector('[data-sn-panel].is-open');
      this.active = opener?.dataset.snOpen || open?.dataset.snPanel || null;
      this.opener = opener;

      this.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-sn-open]');
        if (opener) {
          e.preventDefault();
          this.openPanel(opener.dataset.snOpen, opener);
        }
      }, { signal });

      this.crumb?.querySelector('[data-sn-back]')?.addEventListener('click', () => this.back(), { signal });

      // Escape goes back one level before it closes the menu. Captured on the
      // document so it runs before ModalElement's handler wherever focus is
      // (a click on the panel header leaves it on <body>); the stamp tells that
      // handler this Escape is taken, and preventDefault stops the browser
      // also sending it to the drawer's CloseWatcher, which would close the
      // whole menu. Android Back takes the same step in MenuDrawer
      // (assets/theme.js, onCloseRequest).
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !this.active || e.__modalEscapeHandled) return;
        if (!this.drawer?.hasAttribute('open')) return;
        // A layer opened over the menu (the country picker) closes first.
        const Modal = customElements.get('modal-element');
        if (Modal?.topMost && Modal.topMost() !== this.drawer) return;
        e.__modalEscapeHandled = true;
        e.preventDefault();
        this.back();
      }, { capture: true, signal });

      // Every close resets to the top level, so the menu always reopens at Shop.
      this.drawer?.addEventListener('drawer:afterHide', () => this.reset(), { signal });

      this.setupSearch(signal);
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    panel(key) {
      return this.querySelector(`[data-sn-panel="${key}"]`);
    }

    openPanel(key, opener) {
      const panel = this.panel(key);
      if (!panel) return;
      if (this.active) this.panel(this.active).hidden = true;
      this.active = key;
      this.opener = opener;
      panel.scrollTop = 0;
      panel.hidden = false;
      panel.classList.toggle('is-instant', reduceMotion());
      requestAnimationFrame(() => panel.classList.add('is-open'));

      opener?.setAttribute('aria-expanded', 'true');
      this.root.inert = true;
      this.root.setAttribute('aria-hidden', 'true');

      if (this.crumb) {
        this.crumb.querySelector('[data-sn-crumb-title]').textContent = panel.dataset.title || '';
        const hi = this.crumb.querySelector('[data-sn-crumb-hi]');
        hi.textContent = panel.dataset.hi || '';
        hi.hidden = !panel.dataset.hi;
        this.crumb.hidden = false;
        this.wordmark?.setAttribute('hidden', '');
      }
      this.drawer?.classList.add('sn-panel-open');
      // Focus the panel itself (labelled by its title) so a screen reader
      // announces where it landed and Tab starts from the top of the panel.
      setTimeout(() => panel.focus({ preventScroll: true }), reduceMotion() ? 0 : 60);
    }

    back() {
      if (!this.active) return;
      const panel = this.panel(this.active);
      const opener = this.opener;
      panel.classList.remove('is-open');
      const hide = () => { if (!panel.classList.contains('is-open')) panel.hidden = true; };
      if (reduceMotion()) hide();
      else { panel.addEventListener('transitionend', hide, { once: true }); setTimeout(hide, 300); }
      this.active = null;
      this.opener = null;
      opener?.setAttribute('aria-expanded', 'false');
      this.root.inert = false;
      this.root.removeAttribute('aria-hidden');
      if (this.crumb) this.crumb.hidden = true;
      this.wordmark?.removeAttribute('hidden');
      this.drawer?.classList.remove('sn-panel-open');
      opener?.focus({ preventScroll: true });
    }

    // Every panel, not just the one this.active names: the menu must reopen
    // at the top level whatever state a close left it in.
    reset() {
      this.querySelectorAll('[data-sn-panel]').forEach((panel) => {
        panel.classList.remove('is-open');
        panel.hidden = true;
      });
      this.querySelectorAll('[data-sn-open][aria-expanded="true"]').forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
      this.active = null;
      this.opener = null;
      this.root.inert = false;
      this.root.removeAttribute('aria-hidden');
      this.root.scrollTop = 0;
      if (this.crumb) this.crumb.hidden = true;
      this.wordmark?.removeAttribute('hidden');
      this.drawer?.classList.remove('sn-panel-open');
      this.clearResults();
    }

    /* Search suggestions — Shopify's predictive search JSON endpoint. The
       form still submits to /search, so this only ever adds. */
    setupSearch(signal) {
      this.form = this.querySelector('[data-sn-search]');
      this.input = this.form?.querySelector('input[name="q"]');
      this.results = this.form?.querySelector('.sn-search__results');
      if (!this.input || !this.results) return;
      let timer = null;
      let controller = null;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = this.input.value.trim();
        if (q.length < 2) { this.clearResults(); return; }
        timer = setTimeout(async () => {
          controller?.abort();
          controller = new AbortController();
          try {
            const root = (window.Shopify?.routes?.root || '/');
            const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=5&resources[options][unavailable_products]=last`;
            const res = await fetch(url, { signal: controller.signal });
            if (!res.ok) throw new Error(res.status);
            const data = await res.json();
            this.renderResults(q, data?.resources?.results || {});
          } catch (err) {
            if (err.name !== 'AbortError') this.clearResults();
          }
        }, 180);
      }, { signal });
    }

    clearResults() {
      if (!this.results) return;
      this.results.hidden = true;
      this.results.textContent = '';
    }

    renderResults(q, results) {
      const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const products = (results.products || []).slice(0, 5);
      const collections = (results.collections || []).slice(0, 2);
      const searchUrl = `${this.form.getAttribute('action')}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
      let html = '';
      collections.forEach((c) => {
        html += `<a class="sn-result sn-result--col" href="${esc(c.url)}"><span class="sn-result__label">${esc(c.title)}</span><span class="sn-result__meta">Collection</span></a>`;
      });
      products.forEach((p) => {
        const img = p.featured_image?.url || p.image;
        const src = img ? `${img}${img.includes('?') ? '&' : '?'}width=96` : '';
        html += `<a class="sn-result" href="${esc(p.url)}">` +
          `<span class="sn-result__thumb">${src ? `<img src="${esc(src)}" alt="" width="36" height="45" loading="lazy">` : ''}</span>` +
          `<span class="sn-result__label">${esc(p.title)}</span>` +
          `<span class="sn-result__meta">${p.available === false ? 'Sold out' : esc(this.money(p.price))}</span></a>`;
      });
      if (!products.length && !collections.length) {
        html = `<p class="sn-result__none">No matches for “${esc(q)}”.</p>`;
      }
      html += `<a class="sn-result sn-result--all" href="${esc(searchUrl)}">See all results for “${esc(q)}”</a>`;
      this.results.innerHTML = html;
      this.results.hidden = false;
    }

    money(price) {
      const n = Number(price);
      if (!Number.isFinite(n)) return '';
      const code = window.Shopify?.currency?.active || 'INR';
      try {
        return new Intl.NumberFormat(code === 'INR' ? 'en-IN' : undefined, { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(n);
      } catch (_) {
        return String(price);
      }
    }
  }

  if (!customElements.get('sunya-drawer-nav')) customElements.define('sunya-drawer-nav', SunyaDrawerNav);
})();
